import { createSlice, PayloadAction, current } from "@reduxjs/toolkit";
import { ReduxState } from "..";
import {
  createCustomFormat,
  deleteCustomFormat,
  getCustomFormats,
  updateCustomFormat,
} from "../../docuclipper/api";
import {
  CustomFormat,
  CustomFormatItem,
  CustomFormatSource,
} from "../../docuclipper/DocuclipperTypes";
import { createAlert } from "./Alerts";
import { setSelectedExportType } from "./DownloadOptions";

const lsKeySelectedCustomConfigCSV = `docuclipper.prefs.selectedCustomConfigCSV`

const defaultData: CustomFormat["data"] = {
  columnConfigs: [
    {
      index: 0,
      source: "date" as CustomFormatSource,
      params: {},
      type: "date",
    },
    {
      index: 1,
      source: "description" as CustomFormatSource,
      params: {},
      type: "text",
    },
    {
      index: 2,
      source: "amount" as CustomFormatSource,
      params: {},
      type: "number",
    },
  ],
};

const initialState: {
  customFormats: CustomFormat[];
  selected: null | CustomFormat;
  loading: boolean;
} = {
  customFormats: [],
  selected: null,
  loading: false,
};

const slice = createSlice({
  name: "customFormat",
  initialState,
  reducers: {
    setcustomFormats(state, action: PayloadAction<CustomFormat[]>) {
      state.customFormats = action.payload;
    },
    setSelectedCustomFormat(state, action: PayloadAction<string>) {
      const cState = current(state);
      const selected = cState.customFormats.filter(
        (x) => x.name === action.payload
      )[0];

      if (selected) {
        state.selected = selected;
      } else {
        state.selected = {
          name: action.payload,
          data: defaultData,
          id: action.payload
        }
      }
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    updateSelected(state, action: PayloadAction<CustomFormat | null>) {
      state.selected = action.payload;
    },
  },
});

export const {
  setcustomFormats,
  setSelectedCustomFormat,
  setLoading,
  updateSelected,
  // updatecustomFormat,
} = slice.actions;


export const createCustomFormatRedux = () => async (dispatch, getState) => {
  const state: ReduxState = getState();
  try {
    const existingNames = state.CustomFormat.customFormats.map((x) => x.name);
    let nameIndex = 1;
    let name = `configuration ${nameIndex}`;
    while (existingNames.includes(name)) {
      nameIndex += 1;
      name = `configuration ${nameIndex}`;
    }

    const cf = await createCustomFormat(name, defaultData);
    try {
      cf.data = JSON.parse(cf.data);
    } catch (err) {
      cf.data = { columnConfigs: [] };
    }

    dispatch(setcustomFormats([...state.CustomFormat.customFormats, cf]));
    dispatch(setSelectedCustomFormat(cf.name));
    dispatch(setSelectedExportType(`custom-${cf.name}` as any));
    localStorage.setItem(lsKeySelectedCustomConfigCSV, JSON.stringify({
      value: `custom-${cf.name}`,
      label: cf.name.charAt(0).toUpperCase() + cf.name.slice(1),
    }))
  } catch (err) {
    //
  }
};

export const deleteSelectedCustomFormat = () => async (dispatch, getState) => {
  const state: ReduxState = getState();
  if (!state.CustomFormat.selected) {
    return;
  }
  try {
    const newFormats = state.CustomFormat.customFormats.filter(
      (x) => x.name !== state.CustomFormat.selected?.name
    );
    dispatch(setcustomFormats(newFormats));
    await deleteCustomFormat(state.CustomFormat.selected.id);

    dispatch(
      setSelectedCustomFormat(newFormats.length > 0 ? newFormats[0].name : "")
    );
    if (newFormats.length === 0) {
      dispatch(updateSelected(null));
    }
  } catch (err) {
    //
  }
};

export const deleteCustomFormatItemRedux =
  (cfi: CustomFormatItem) => async (dispatch, getState) => {
    const state: ReduxState = getState();
    if (!state.CustomFormat.selected) {
      return;
    }

    const newSelected = {
      ...state.CustomFormat.selected,
      data: {
        ...state.CustomFormat.selected.data,
        columnConfigs: state.CustomFormat.selected.data.columnConfigs
          .filter((x) => x.index !== cfi.index)
          .map((x, i) => ({ ...x, index: i })),
      },
    };

    try {
      await updateCustomFormat(newSelected);
      dispatch(updateSelected(newSelected));
    } catch (err) {
      //
      dispatch(
        createAlert({
          message: "Error updating configuration",
          timeout: 0,
          type: "error",
        })
      );
    }
  };

export const addCustomFormatItemRedux = () => async (dispatch, getState) => {
  const state: ReduxState = getState();
  if (!state.CustomFormat.selected) {
    return;
  }

  const newCfi: CustomFormatItem = {
    index: state.CustomFormat.selected.data.columnConfigs.length,
    params: {},
    type: "date",
    source: "date" as CustomFormatSource,
  };
  const newSelected = {
    ...state.CustomFormat.selected,
    data: {
      ...state.CustomFormat.selected.data,
      columnConfigs: [
        ...state.CustomFormat.selected.data.columnConfigs,
        newCfi,
      ],
    },
  };

  try {
    await updateCustomFormat(newSelected);
    dispatch(updateSelected(newSelected));
  } catch (err) {
    //
    dispatch(
      createAlert({
        message: "Error updating configuration",
        timeout: 0,
        type: "error",
      })
    );
  }
};

export const updateCustomFormatName =
  (name: string) => async (dispatch, getState) => {
    const state: ReduxState = getState();
    if (!state.CustomFormat.selected) {
      return;
    }

    const newSelected = {
      ...state.CustomFormat.selected,
      name,
    };

    const newFormats = state.CustomFormat.customFormats.map((x) =>
      x.name === state.CustomFormat.selected?.name ? { ...x, name } : x
    );
    try {
      await updateCustomFormat(newSelected);
      dispatch(updateSelected(newSelected));
      dispatch(setcustomFormats(newFormats));
      dispatch(setSelectedExportType(`custom-${newSelected.name}` as any));
    } catch (err) {
      dispatch(
        createAlert({
          message: "Error updating configuration",
          timeout: 0,
          type: "error",
        })
      );
    }
  };

export const updateCustomFormatItemRedux =
  (cfi: CustomFormatItem) => async (dispatch, getState) => {
    const state: ReduxState = getState();
    if (!state.CustomFormat.selected) {
      return;
    }
    if (!state.CustomFormat.selected) {
      return;
    }

    const newSelected = {
      ...state.CustomFormat.selected,
      data: {
        ...state.CustomFormat.selected.data,
        columnConfigs: state.CustomFormat.selected.data.columnConfigs.map((x) =>
          x.index === cfi.index ? { ...x, ...cfi } : x
        ),
      },
    };

    try {
      await updateCustomFormat(newSelected);
      dispatch(updateSelected(newSelected));
    } catch (err) {
      dispatch(
        createAlert({
          message: "Error updating configuration",
          timeout: 0,
          type: "error",
        })
      );
    }
  };

type CustomFormatsProps = {
  init: boolean;
}

export const fetchCustomFormats = ({
  init
}: CustomFormatsProps) => async (dispatch, getState) => {
  const state: ReduxState = getState();
  if (state.CustomFormat.loading) {
    return;
  }

  try {
    dispatch(setLoading(true));
    const customFormats = await getCustomFormats();
    dispatch(setLoading(false));
    dispatch(
      setcustomFormats(
        customFormats.map((x) => {
          let data = { columnConfigs: [] };
          try {
            data = JSON.parse(x.data);
          } catch (err) {
            //
            console.error(err);
          }
          return { ...x, data };
        })
      )
    );
    if (customFormats.length > 0 && !state.CustomFormat.selected && init) {
      const cf = customFormats[0];
      dispatch(setSelectedCustomFormat(cf.name));
      dispatch(setSelectedExportType(`custom-${cf.name}` as any));
    }
  } catch (err) {
    dispatch(setLoading(false));
    //
  }
};

export default slice.reducer;
